/**
 * utils/descriptionHtml.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE normalization step for product description HTML before it is rendered
 * (always still passed through DOMPurify afterwards). Used by BOTH the
 * quick-view modal and the dedicated product page so every listing renders
 * identically — this is the render half of the WYSIWYG contract with the
 * dashboard editor (frontend/src/utilities/richText.js, keep in sync).
 *
 * It does two things:
 *
 * 1. Heals double-encoded entities (`&amp;amp;` → `&amp;`) left behind by a
 *    pre-July-2026 server sanitizer bug, so legacy rows don't render a
 *    literal "&amp;". (The server now heals stored rows on re-save.)
 *
 * 2. Upgrades legacy plain-text bullets to REAL lists: descriptions written
 *    before the rich editor have literal "• " characters inside <p> lines.
 *    Consecutive bullet paragraphs are grouped into <ul><li>…</li></ul> so
 *    they get the standard, pronounced list styling instead of faint
 *    text dots. (The dashboard editor does the same lift at edit time, so a
 *    re-saved product arrives here already semantic.)
 */

const BULLET_PREFIX_RE = /^\s*(?:•|·|▪|◦|\*|-)(?:&nbsp;|\s)+/;

/** Collapse `&amp;amp;` (any depth, any of the common entities) to one level. */
export function healDoubleEncodedEntities(html) {
  let prev;
  let cur = String(html);
  do {
    prev = cur;
    cur = cur.replace(/&amp;(amp|lt|gt|quot|#39|nbsp);/gi, "&$1;");
  } while (cur !== prev);
  return cur;
}

/** Group consecutive `<p>• …</p>` paragraphs into a semantic `<ul>`. */
export function bulletParagraphsToLists(html) {
  const parts = String(html).split(/(<p>[\s\S]*?<\/p>)/gi).filter(Boolean);
  const out = [];
  let items = [];
  const flush = () => {
    if (items.length) {
      out.push(`<ul>${items.map((li) => `<li>${li}</li>`).join("")}</ul>`);
      items = [];
    }
  };
  for (const part of parts) {
    const m = part.match(/^<p>([\s\S]*?)<\/p>$/i);
    if (m && BULLET_PREFIX_RE.test(m[1])) {
      items.push(m[1].replace(BULLET_PREFIX_RE, "").trim());
    } else {
      flush();
      out.push(part);
    }
  }
  flush();
  return out.join("");
}

/** The one entry point: heal entities, then upgrade legacy bullets. */
export function normalizeDescriptionHtml(html) {
  if (!html) return "";
  return bulletParagraphsToLists(healDoubleEncodedEntities(html));
}

export default normalizeDescriptionHtml;
